import moment from 'moment';

import { evaluateCondition } from '../config/evaluateCondition';
import { isConfigEnabled } from '../config/isConfigEnabled';
import {
    getStatusTransitionConfigFromBlocks,
    resolveNegotiationConfigFromStatusTransition,
    resolveNegotiationStatusOptions,
} from '../config/resolveTrackerConfig';

export const TOP_TIER_REQUIRED_FIELD = 'topTierRequired';
export const APPROVAL_DATE_FIELD = 'approvalDate';
export const NEGOTIATION_STATUS_FIELD = 'negotiationStatus';

export const DEAL_REVIEW_STATUS = 'Deal Review';
export const NEW_NEGOTIATION_STATUS = 'New Negotiation';
export const NEGOTIATION_IN_PROGRESS_STATUS = 'Negotiation In Progress';
export const NEGOTIATION_COMPLETED_STATUS = 'Negotiation Completed';
export const NEGOTIATION_CANCELLED_STATUS = 'Negotiation Cancelled';

export const TIER_APPROVAL_DATE_FIELDS = {
    0: 'tier0ApprovalDate',
    1: 'tier1ApprovalDate',
    2: 'tier2ApprovalDate',
};

const DEFAULT_NEGOTIATION_OPTIONS = [
    DEAL_REVIEW_STATUS,
    NEW_NEGOTIATION_STATUS,
    NEGOTIATION_IN_PROGRESS_STATUS,
    NEGOTIATION_COMPLETED_STATUS,
    NEGOTIATION_CANCELLED_STATUS,
];

/**
 * Builds negotiation runtime config from blocks (statusTransition in block.config).
 */
export const resolveNegotiationConfigFromBlocks = (blocks) => {
    const statusTransition = getStatusTransitionConfigFromBlocks(blocks);
    return resolveNegotiationConfigFromStatusTransition(statusTransition);
};

/**
 * Returns negotiation status dropdown options from config or defaults.
 */
export const getNegotiationStatusOptions = (negotiationConfig, elementOptions) => {
    if (negotiationConfig?.options?.length) {
        return negotiationConfig.options.map((status) => ({
            label: status,
            value: status,
        }));
    }

    return resolveNegotiationStatusOptions(null, elementOptions);
};

/**
 * Returns true when a date field holds a valid non-empty value.
 */
export const hasApprovalDate = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    return moment.parseZone(value).isValid();
};

/**
 * Maps topTierRequired to the corresponding tier approval date field key.
 */
export const getTopTierApprovalDateKey = (topTierRequired, negotiationConfig) => {
    const tierMatch = topTierRequired == null
        ? null
        : String(topTierRequired).match(/tier\s*(\d+)/i);
    const tierNumber = tierMatch ? parseInt(tierMatch[1], 10) : null;
    const tierApprovalDateFields = negotiationConfig?.tierApprovalDateFields
        || negotiationConfig?.autoUpdate?.tierApprovalDateFields
        || TIER_APPROVAL_DATE_FIELDS;

    if (tierNumber == null) {
        return null;
    }

    return tierApprovalDateFields[tierNumber] || null;
};

/**
 * Returns true when the row's relevant top-tier approval date has a value.
 */
export const hasRelevantTopTierApprovalDate = (row = {}, negotiationConfig) => {
    const topTierField = negotiationConfig?.topTierField || TOP_TIER_REQUIRED_FIELD;
    const approvalDateKey = getTopTierApprovalDateKey(row[topTierField], negotiationConfig);

    if (!approvalDateKey) {
        return false;
    }

    return hasApprovalDate(row[approvalDateKey]);
};

/**
 * Default negotiation status when no derive rule matches.
 */
export const getDefaultNegotiationStatus = (negotiationConfig) => {
    const rules = negotiationConfig?.autoUpdate?.rules || [];
    const defaultRule = rules.find((rule) => rule.condition?.operator === 'default' || rule.when?.operator === 'default');

    return defaultRule?.set || DEAL_REVIEW_STATUS;
};

/**
 * Derives negotiationStatus from configured priority rules (deriveRules).
 */
export const deriveNegotiationStatus = (row = {}, negotiationConfig) => {
    if (!negotiationConfig || !isConfigEnabled(negotiationConfig.autoUpdate)) {
        if (hasApprovalDate(row[APPROVAL_DATE_FIELD])) {
            return NEGOTIATION_COMPLETED_STATUS;
        }

        if (hasRelevantTopTierApprovalDate(row, negotiationConfig)) {
            return NEGOTIATION_IN_PROGRESS_STATUS;
        }

        return DEAL_REVIEW_STATUS;
    }

    const context = {
        topTierField: negotiationConfig.topTierField || TOP_TIER_REQUIRED_FIELD,
        tierApprovalDateFields: negotiationConfig.autoUpdate?.tierApprovalDateFields || TIER_APPROVAL_DATE_FIELDS,
    };

    const enabledRules = (negotiationConfig.autoUpdate.rules || [])
        .filter((rule) => isConfigEnabled(rule))
        .sort((left, right) => (left.priority || 99) - (right.priority || 99));

    for (const rule of enabledRules) {
        const condition = rule.condition || rule.when;
        if (evaluateCondition(row, condition, context)) {
            return rule.set;
        }
    }

    return getDefaultNegotiationStatus(negotiationConfig);
};

/**
 * Returns true when saving this field can affect negotiationStatus.
 */
const isNegotiationStatusTriggerField = (fieldKey, row, negotiationConfig) => {
    const autoUpdate = negotiationConfig?.autoUpdate;
    const topTierField = negotiationConfig?.topTierField || TOP_TIER_REQUIRED_FIELD;
    const approvalDateField = negotiationConfig?.approvalDateField || APPROVAL_DATE_FIELD;

    const alwaysTriggers = autoUpdate?.triggerFields?.always || [
        approvalDateField,
        topTierField,
        'negotiationCompletionDate',
    ];

    if (alwaysTriggers.includes(fieldKey)) {
        return true;
    }

    if (autoUpdate?.triggerFields?.includeRelevantTierApprovalDate === false) {
        return false;
    }

    const tierApprovalFields = Object.values(
        autoUpdate?.tierApprovalDateFields || TIER_APPROVAL_DATE_FIELDS,
    );

    if (tierApprovalFields.includes(fieldKey)) {
        const topTierVal = row[topTierField];
        const tierMatch = topTierVal == null ? null : String(topTierVal).match(/tier\s*(\d+)/i);
        const keyTier = fieldKey.match(/tier(\d+)/i)?.[1];
        if (tierMatch && keyTier) {
            return tierMatch[1] === keyTier;
        }
        return true;
    }

    return false;
};

/**
 * Returns true when automatic negotiationStatus updates should be skipped.
 */
const shouldSkipNegotiationStatusAutoUpdate = (row, fieldKey, negotiationConfig) => {
    const statusField = negotiationConfig?.field || NEGOTIATION_STATUS_FIELD;
    const skipField = negotiationConfig?.autoUpdate?.skipWhenSavingField || statusField;

    if (fieldKey === skipField) {
        return true;
    }

    const skipValues = negotiationConfig?.autoUpdate?.skipWhenCurrentValueIn
        || negotiationConfig?.skipWhenCurrentValueIn
        || [NEGOTIATION_CANCELLED_STATUS];

    return skipValues.includes(row?.[statusField]);
};

/**
 * Returns negotiationStatus payload when a trigger field is saved or cleared.
 */
export const getNegotiationStatusAutoUpdatePayload = (
    fieldKey,
    value,
    row = {},
    negotiationConfig,
) => {
    if (!negotiationConfig || !isConfigEnabled(negotiationConfig.autoUpdate)) {
        return {};
    }

    if (!row || shouldSkipNegotiationStatusAutoUpdate(row, fieldKey, negotiationConfig)) {
        return {};
    }

    const statusField = negotiationConfig.field || NEGOTIATION_STATUS_FIELD;
    const topTierField = negotiationConfig.topTierField || TOP_TIER_REQUIRED_FIELD;
    const effectiveTopTier = fieldKey === topTierField ? value : row[topTierField];
    const mergedRow = { ...row, [fieldKey]: value, [topTierField]: effectiveTopTier };

    if (!isNegotiationStatusTriggerField(fieldKey, mergedRow, negotiationConfig)) {
        return {};
    }

    const derivedStatus = deriveNegotiationStatus(mergedRow, negotiationConfig);
    const currentStatus = row[statusField];

    if (derivedStatus === currentStatus) {
        return {};
    }

    return { [statusField]: derivedStatus };
};
